import { useMemo, useState } from 'react';
import { faGavel } from '@fortawesome/free-solid-svg-icons';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { Area, AzDrawer, Bubble, Btn, Chip, FChip, Sec, Sel } from '../../components/az';
import { useHandOff } from '../../queries/actions';
import { clock, firstName, tzAbbr } from '../../utils/actions';
import { formatPhone } from '../../utils/callLog';
import * as S from './styled';

const TIMES = ['9:00 AM', '10:00 AM', '11:00 AM', '1:00 PM', '2:00 PM', '3:00 PM'];

function zonedIso(dayIso, time, tz) {
  const [, h, m, ap] = /(\d+):(\d+) (AM|PM)/.exec(time);
  const hour = (Number(h) % 12) + (ap === 'PM' ? 12 : 0);
  const guess = new Date(`${dayIso}T${String(hour).padStart(2, '0')}:${m}:00Z`);
  const local = new Date(guess.toLocaleString('en-US', { timeZone: tz }));
  const utc = new Date(guess.toLocaleString('en-US', { timeZone: 'UTC' }));
  return new Date(guess.getTime() + (utc - local)).toISOString();
}

function nextDays(tz, now = new Date()) {
  const days = [];
  for (let d = 1; days.length < 3 && d < 10; d += 1) {
    const at = new Date(now.getTime() + d * 86400000);
    const weekday = new Intl.DateTimeFormat('en-US', { timeZone: tz, weekday: 'short' }).format(at);
    if (weekday === 'Sat' || weekday === 'Sun') continue;
    const label = new Intl.DateTimeFormat('en-US', { timeZone: tz, weekday: 'short', month: 'short', day: 'numeric' }).format(at).replace(',', '');
    const iso = new Intl.DateTimeFormat('en-CA', { timeZone: tz }).format(at);
    days.push([iso, label]);
  }
  return days;
}

export default function HandOffDrawer({ contact, agent, orgName, tz, openNeed, showingAt, onClose }) {
  const navigate = useNavigate();
  const handOff = useHandOff();
  const first = firstName(contact.name);
  const days = useMemo(() => nextDays(tz), [tz]);
  const quick = [
    ['follow', `Have ${agent} follow up`, 'follows up', `Hi ${first}, this is ${agent}. Checking in on your question. Want me to text you anything, or set up a time with our leasing team?`],
    ['movein', 'Ask for move-in date', 'asks for the move-in date', `Hi ${first}, this is ${agent}. What month are you hoping to move in?`],
    showingAt && ['remind', 'Remind about the showing', 'reminds about the showing', `Hi ${first}, reminder: your tour is ${new Intl.DateTimeFormat('en-US', { timeZone: tz, weekday: 'long' }).format(new Date(showingAt))} at ${clock(showingAt, tz)} ${tzAbbr(tz)}.`]
  ].filter(Boolean);
  const [act, setAct] = useState('follow');
  const [free, setFree] = useState('');
  const [day, setDay] = useState(days[0]?.[0] || 'now');
  const [time, setTime] = useState('10:00 AM');
  const [alsoResolve, setAlsoResolve] = useState(false);

  const picked = quick.find((q) => q[0] === act) || quick[0];
  const body = free || picked[3];
  const text = `${orgName}: ${body} Reply STOP to opt out.`;
  const rule = act === 'remind' ? 'Q10 (staff-confirmed time)' : 'Q1 (website facts only)';

  const go = () =>
    handOff.mutate(
      {
        contact,
        text: body,
        label: free ? 'does what you asked' : picked[2],
        runAt: day === 'now' ? null : zonedIso(day, time, tz),
        alsoResolveId: alsoResolve ? openNeed?._id : null
      },
      {
        onSuccess: ({ task }) => {
          onClose();
          toast(`Handed to ${agent}. See it in Agent Actions.`, {
            action: { label: 'View', onClick: () => navigate('/todos', { state: { openActionId: task._id } }) }
          });
        }
      }
    );

  return (
    <AzDrawer title={`Hand off to ${agent}`} onClose={onClose}>
      <S.Col $gap={12}>
        <S.Sm>
          <b>{contact.name || (contact.phone && formatPhone(contact.phone))}</b>
        </S.Sm>
        <Sec title="Quick actions">
          <S.Row $gap={4}>
            {quick.map((q) => (
              <FChip key={q[0]} style={{ minHeight: 48 }} aria-pressed={act === q[0] && !free} onClick={() => {
                  setAct(q[0]);
                  setFree('');
                }}>
                {q[1]}
              </FChip>
            ))}
          </S.Row>
        </Sec>
        <Sec title={`Or tell ${agent} what to do`}>
          <Area
            rows={2}
            maxLength={200}
            value={free}
            placeholder="Follow up about their question ..."
            onChange={(e) => setFree(e.target.value)}
            aria-label={`Tell ${agent} what to do`}
          />
          <S.Xs $muted>
            {agent} only uses facts from your sources. She won't promise anything outside them. {free.length} / 200
          </S.Xs>
        </Sec>
        <Sec title="When">
          <S.Row>
            <Sel value={day} onChange={(e) => setDay(e.target.value)} aria-label="Day">
              <option value="now">Now</option>
              {days.map(([iso, label]) => (
                <option key={iso} value={iso}>
                  {label}
                </option>
              ))}
            </Sel>
            {day !== 'now' && (
              <Sel value={time} onChange={(e) => setTime(e.target.value)} aria-label="Time">
                {TIMES.map((t) => (
                  <option key={t}>{t}</option>
                ))}
              </Sel>
            )}
            <S.Muted>{tzAbbr(tz)}</S.Muted>
          </S.Row>
        </Sec>
        <Sec title={`Preview (what ${agent} will send)`}>
          <S.SubCard $pad={20} $gap={4}>
            <S.Xs style={{ fontWeight: 600 }}>{orgName}</S.Xs>
            <Bubble>{text}</Bubble>
          </S.SubCard>
          <S.Row $gap={4}>
            <Chip family="neutral" icon={faGavel}>
              Rule: {rule}
            </Chip>
          </S.Row>
        </Sec>
        {openNeed && (
          <S.Radio>
            <input type="checkbox" checked={alsoResolve} onChange={() => setAlsoResolve(!alsoResolve)} />
            Also resolve: {openNeed.title}
          </S.Radio>
        )}
        <S.Row>
          <Btn onClick={onClose}>Cancel</Btn>
          <div style={{ flex: 1 }} />
          <Btn kind="primary" onClick={go} disabled={handOff.isPending}>
            Hand off to {agent}
          </Btn>
        </S.Row>
      </S.Col>
    </AzDrawer>
  );
}
